"use client"

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import React from "react"
import { Button } from "~/components/ui/button"
import { cn } from "~/utilities"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

interface CarouselProps {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!(api && setApi)) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("select", onSelect)
    }
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <section
        aria-label='carousel'
        className={cn("relative", className)}
        data-slot='carousel'
        onKeyDownCapture={handleKeyDown}
        {...props}
      >
        {children}
      </section>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      className='h-full w-full overflow-hidden'
      data-slot='carousel-content'
      ref={carouselRef}
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()

  return (
    // biome-ignore lint/a11y/useSemanticElements: <-- Carousel item as div for flexibility --->
    <div
      aria-label='slide'
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      )}
      data-slot='carousel-item'
      role='group'
      {...props}
    />
  )
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  internal = false,
  ...props
}: React.ComponentProps<typeof Button> & { internal?: boolean }) {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  const isHorizontal = orientation === "horizontal"
  const isInternalNext = internal && canScrollNext

  const positionClassesInternal = isHorizontal
    ? "top-1/2 right-0 -translate-y-1/2"
    : "bottom-0 left-1/2 -translate-x-1/2"

  const positionClassesExternal = isHorizontal
    ? "top-1/2 -right-12 -translate-y-1/2"
    : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90"

  const positionClasses = internal
    ? positionClassesInternal
    : positionClassesExternal

  const styleClasses = internal
    ? cn(
        "size-5 rounded-none bg-black/40 hover:bg-black/50 disabled:opacity-0",
        isHorizontal ? "h-full" : "w-full"
      )
    : "size-5 rounded-full"

  const visibilityClasses =
    "group-hover:opacity-100 disabled:group-hover:opacity-20"
  const animationClasses = isInternalNext
    ? "fade-in animate-in duration-300"
    : ""

  const rotationClass = isHorizontal ? "" : "rotate-90"
  const icon = internal ? (
    <ChevronRight className={cn("text-white", rotationClass)} />
  ) : (
    <ArrowRight className={rotationClass} />
  )
  const dataSlot = internal ? "carousel-next-internal" : "carousel-next"
  const ariaLabel = "Próximo slide"

  return (
    <Button
      aria-label={ariaLabel}
      className={cn(
        "absolute transition-opacity duration-300",
        styleClasses,
        positionClasses,
        visibilityClasses,
        animationClasses,
        className
      )}
      data-slot={dataSlot}
      disabled={!canScrollNext}
      onClick={scrollNext}
      size={size}
      variant={variant}
      {...props}
    >
      {icon}
    </Button>
  )
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  internal = false,
  ...props
}: React.ComponentProps<typeof Button> & { internal?: boolean }) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  const isHorizontal = orientation === "horizontal"
  const isInternalPrev = internal && canScrollPrev

  const positionClassesInternal = isHorizontal
    ? "top-1/2 left-0 -translate-y-1/2"
    : "top-0 left-1/2 -translate-x-1/2"

  const positionClassesExternal = isHorizontal
    ? "top-1/2 -left-12 -translate-y-1/2"
    : "-top-12 left-1/2 -translate-x-1/2 rotate-90"

  const positionClasses = internal
    ? positionClassesInternal
    : positionClassesExternal

  const styleClasses = internal
    ? cn(
        "size-5 rounded-none bg-black/40 hover:bg-black/50 disabled:opacity-0",
        isHorizontal ? "h-full" : "w-full"
      )
    : "size-5 rounded-full"

  const visibilityClasses =
    "group-hover:opacity-100 disabled:group-hover:opacity-20"
  const animationClasses = isInternalPrev
    ? "fade-in animate-in duration-300"
    : ""

  const rotationClass = isHorizontal ? "" : "rotate-90"
  const icon = internal ? (
    <ChevronLeft className={cn("text-white", rotationClass)} />
  ) : (
    <ArrowLeft className={rotationClass} />
  )
  const dataSlot = internal ? "carousel-previous-internal" : "carousel-previous"
  const ariaLabel = "Slide anterior"

  return (
    <Button
      aria-label={ariaLabel}
      className={cn(
        "absolute transition-opacity duration-300",
        styleClasses,
        positionClasses,
        visibilityClasses,
        animationClasses,
        className
      )}
      data-slot={dataSlot}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      size={size}
      variant={variant}
      {...props}
    >
      {icon}
    </Button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
}
